import { useMemo, useState } from "react";
import {
  useCreate,
  useDataProvider,
  useGetList,
  useNotify,
  useRefresh,
} from "ra-core";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  FileText,
  LinkIcon,
  Plus,
  Search,
  Zap,
} from "lucide-react";
import { PageHeader } from "../common/PageHeader";
import type { CrmDataProvider } from "../../providers/types";

type ReceiptRow = {
  id: number;
  reference: string;
  customer_id: number | null;
  receipt_date: string;
  amount: number;
  allocated_amount: number;
  payment_method: string;
  bank_reference: string | null;
  narration: string | null;
  status: "unallocated" | "partial" | "allocated";
};

type CompanyOpt = { id: number; name: string };

type OpenInvoice = {
  id: number;
  reference: string;
  due_date: string | null;
  total_amount: number;
  paid_amount: number;
  payment_status: string | null;
};

const fmtINR = (n: number | null | undefined) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const Label = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <label className={`block text-sm font-medium leading-none ${className ?? ""}`}>
    {children}
  </label>
);

export const ReceiptMatchingPage = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [allocOpen, setAllocOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [active, setActive] = useState<ReceiptRow | null>(null);
  const [openInvoices, setOpenInvoices] = useState<OpenInvoice[]>([]);
  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState(false);

  const [search, setSearch] = useState("");
  const [newReceipt, setNewReceipt] = useState({
    customer_id: "",
    amount: "",
    payment_method: "neft",
    receipt_date: new Date().toISOString().slice(0, 10),
    bank_reference: "",
    narration: "",
  });

  const { data, isLoading } = useGetList<ReceiptRow>("payment_receipts", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "receipt_date", order: "DESC" },
    filter: { "status@neq": "allocated" },
  });

  const { data: companies } = useGetList<CompanyOpt>("companies", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });

  const rows = useMemo(
    () =>
      (data ?? []).filter((row) =>
        search
          ? [row.reference, row.bank_reference, row.narration]
              .filter(Boolean)
              .some((field) =>
                (field as string).toLowerCase().includes(search.toLowerCase()),
              )
          : true,
      ),
    [data, search],
  );

  const customerNameById = useMemo(() => {
    const map = new Map<number, string>();
    (companies ?? []).forEach((c) => map.set(c.id, c.name));
    return map;
  }, [companies]);

  const openAllocation = async (row: ReceiptRow) => {
    setActive(row);
    setPicked([]);
    setPosted(false);
    setAllocOpen(true);
    if (row.customer_id) {
      try {
        const res = await dataProvider.getList<OpenInvoice>("customer_invoices", {
          pagination: { page: 1, perPage: 200 },
          sort: { field: "due_date", order: "ASC" },
          filter: {
            "company_id@eq": row.customer_id,
            "payment_status@neq": "paid",
          },
        });
        setOpenInvoices((res.data ?? []).filter(
          (inv) => Number(inv.total_amount) > Number(inv.paid_amount ?? 0),
        ));
      } catch (error) {
        notify(
          `Could not load open invoices: ${(error as Error).message}`,
          { type: "error" },
        );
      }
    } else {
      setOpenInvoices([]);
    }
  };

  const togglePick = (id: number) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const totalSelected = useMemo(
    () =>
      openInvoices
        .filter((inv) => picked.includes(inv.id))
        .reduce(
          (acc, inv) =>
            acc + (Number(inv.total_amount) - Number(inv.paid_amount ?? 0)),
          0,
        ),
    [openInvoices, picked],
  );

  const allocate = async (mode: "manual" | "fifo") => {
    if (!active) return;
    setBusy(true);
    try {
      const result = await dataProvider.allocateReceipt({
        receiptId: active.id,
        invoiceIds: mode === "manual" ? picked : [],
        mode,
      });
      const count = result.allocations?.length ?? 0;
      notify(`Allocated ${count} invoice(s).`, { type: "success" });
      setPosted(true);
      refresh();
      setTimeout(() => {
        setAllocOpen(false);
        setActive(null);
      }, 1200);
    } catch (error) {
      notify(`Allocation failed: ${(error as Error).message}`, {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const submitReceipt = async () => {
    if (!newReceipt.amount || !newReceipt.customer_id) {
      notify("Customer and amount are required.", { type: "warning" });
      return;
    }
    try {
      await create(
        "payment_receipts",
        {
          data: {
            customer_id: Number(newReceipt.customer_id),
            amount: Number(newReceipt.amount),
            payment_method: newReceipt.payment_method,
            receipt_date: newReceipt.receipt_date,
            bank_reference: newReceipt.bank_reference || null,
            narration: newReceipt.narration || null,
            status: "unallocated",
          },
        },
        { returnPromise: true },
      );
      notify("Receipt logged.", { type: "success" });
      setCreateOpen(false);
      setNewReceipt({
        customer_id: "",
        amount: "",
        payment_method: "neft",
        receipt_date: new Date().toISOString().slice(0, 10),
        bank_reference: "",
        narration: "",
      });
      refresh();
    } catch (error) {
      notify(`Failed: ${(error as Error).message}`, { type: "error" });
    }
  };

  return (
    <div className="p-6 bg-slate-50/30 min-h-screen">
      <PageHeader
        title="Receipt & Influx Matching"
        description="Record customer receipts and allocate them across open invoices."
      />

      <Card className="shadow-none border border-slate-200 bg-white overflow-hidden">
        <CardHeader className="border-b bg-slate-50/50 py-4 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-slate-500" /> Unallocated Receipts
          </CardTitle>
          <div className="flex gap-2 items-center">
            <div className="relative max-w-xs hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search ref / UTR…"
                className="h-8 pl-8 border-slate-300 text-xs shadow-none w-[180px] bg-white"
              />
            </div>
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-slate-900 hover:bg-slate-800 font-bold text-white shadow-md text-xs h-8 gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" /> Log Receipt
            </Button>
          </div>
        </CardHeader>

        <div className="w-full overflow-x-auto border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-slate-50 border-b-2 border-slate-200">
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Receipt Ref
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Customer / Bank ref
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Date
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Amount
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Unallocated
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 text-right">
                  Operation
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-slate-400"
                  >
                    Loading receipts…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-slate-400"
                  >
                    No unallocated receipts. Log a new one to start matching.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const unallocated =
                  Number(row.amount) - Number(row.allocated_amount ?? 0);
                return (
                  <tr
                    key={row.id}
                    className="even:bg-slate-50/50 hover:bg-slate-100/50 border-b border-slate-200 transition-colors"
                  >
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-mono font-bold text-primary align-middle">
                      {row.reference}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 align-middle">
                      <div className="font-bold text-slate-900">
                        {row.customer_id
                          ? customerNameById.get(row.customer_id) ??
                            `Customer #${row.customer_id}`
                          : "(unassigned)"}
                      </div>
                      <div className="text-xs font-mono text-slate-500 mt-0.5">
                        {row.bank_reference ?? row.narration ?? ""}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 text-slate-500 align-middle">
                      {fmtDate(row.receipt_date)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-black text-slate-900 text-base align-middle">
                      {fmtINR(row.amount)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-bold text-amber-600 align-middle">
                      {fmtINR(unallocated)}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-right align-middle">
                      <Button
                        size="sm"
                        onClick={() => openAllocation(row)}
                        className="text-xs h-8 font-bold bg-slate-900 hover:bg-slate-800 shadow-sm"
                      >
                        Allocate
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Allocation sheet */}
      <Sheet open={allocOpen} onOpenChange={setAllocOpen}>
        <SheetContent className="sm:max-w-[520px] p-0 bg-white flex flex-col h-full border-l border-slate-200">
          {active && (
            <>
              <SheetHeader className="p-6 bg-slate-900 text-white">
                <SheetTitle className="text-white text-xl font-bold flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-emerald-400" /> Allocate Funds
                </SheetTitle>
                <SheetDescription className="text-slate-400 font-medium mt-1">
                  Allocating {fmtINR(active.amount)} from{" "}
                  {active.customer_id
                    ? customerNameById.get(active.customer_id) ??
                      `Customer #${active.customer_id}`
                    : "(unassigned)"}
                </SheetDescription>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {!posted ? (
                  <>
                    <div className="flex justify-end">
                      <Button
                        onClick={() => allocate("fifo")}
                        disabled={busy}
                        size="sm"
                        className="h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Zap className="h-3.5 w-3.5" /> Auto-FIFO
                      </Button>
                    </div>
                    <div>
                      <Label className="text-slate-500 font-bold text-xs uppercase tracking-wider mb-3 block">
                        Step 1 · Select open invoices
                      </Label>
                      <div className="space-y-3">
                        {openInvoices.length === 0 && (
                          <div className="text-sm text-slate-400 italic">
                            No open invoices for this customer.
                          </div>
                        )}
                        {openInvoices.map((inv) => {
                          const open =
                            Number(inv.total_amount) -
                            Number(inv.paid_amount ?? 0);
                          const isPicked = picked.includes(inv.id);
                          return (
                            <div
                              key={inv.id}
                              onClick={() => togglePick(inv.id)}
                              className={`p-4 border rounded-xl cursor-pointer flex justify-between items-center transition-all ${
                                isPicked
                                  ? "border-primary bg-blue-50/50 shadow-md scale-[1.02]"
                                  : "border-slate-200 hover:bg-slate-50"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className={`h-6 w-6 rounded-full border flex items-center justify-center transition-colors ${
                                    isPicked
                                      ? "bg-primary border-primary"
                                      : "bg-white border-slate-300"
                                  }`}
                                >
                                  {isPicked && (
                                    <Check className="h-3.5 w-3.5 text-white" />
                                  )}
                                </div>
                                <div>
                                  <div className="font-bold text-sm text-slate-900 flex items-center gap-1.5">
                                    <FileText className="h-4 w-4 text-slate-400" />
                                    {inv.reference}
                                  </div>
                                  <div className="text-xs text-slate-500 mt-0.5">
                                    Due {fmtDate(inv.due_date)}
                                  </div>
                                </div>
                              </div>
                              <div className="font-black text-slate-900 text-base">
                                {fmtINR(open)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 space-y-3 shadow-inner mt-8">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600 font-bold">
                          Settlement stacked:
                        </span>
                        <span className="font-black text-slate-900 text-lg">
                          {fmtINR(totalSelected)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-slate-200 pt-3">
                        <span className="text-slate-600 font-medium">
                          Remaining receipt:
                        </span>
                        <span
                          className={`font-black text-lg ${
                            active.amount - totalSelected < 0
                              ? "text-red-600"
                              : "text-emerald-600"
                          }`}
                        >
                          {fmtINR(active.amount - totalSelected)}
                        </span>
                      </div>
                      <div className="pt-2">
                        <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden shadow-sm">
                          <div
                            className={`h-full transition-all duration-500 ease-out ${
                              totalSelected > active.amount
                                ? "bg-red-500"
                                : "bg-emerald-500"
                            }`}
                            style={{
                              width: `${Math.min(
                                (totalSelected / Number(active.amount || 1)) * 100,
                                100,
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center space-y-4 animate-in zoom-in-95">
                    <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center shadow-lg">
                      <CheckCircle2 className="h-12 w-12 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      Allocation posted
                    </h3>
                    <p className="text-slate-500 font-medium text-center max-w-xs">
                      Receipt allocations recorded and invoice balances updated.
                    </p>
                  </div>
                )}
              </div>

              {!posted && (
                <SheetFooter className="p-6 border-t bg-slate-50">
                  <Button
                    onClick={() => allocate("manual")}
                    disabled={busy || picked.length === 0}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md text-lg h-12 gap-3 transition-transform active:scale-95"
                  >
                    Post Matching <ArrowRight className="h-5 w-5" />
                  </Button>
                </SheetFooter>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Log new receipt */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent className="sm:max-w-[420px] p-0 flex flex-col bg-white border-l h-full">
          <SheetHeader className="p-6 border-b">
            <SheetTitle>Log Customer Receipt</SheetTitle>
            <SheetDescription>
              Record an incoming payment. Allocate it to invoices in the next step.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="space-y-1.5">
              <Label>Customer</Label>
              <select
                value={newReceipt.customer_id}
                onChange={(e) =>
                  setNewReceipt({ ...newReceipt, customer_id: e.target.value })
                }
                className="w-full h-11 px-3 border border-slate-300 bg-white rounded-md font-bold text-slate-900"
              >
                <option value="">— select —</option>
                {(companies ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount</Label>
                <Input
                  type="number"
                  value={newReceipt.amount}
                  onChange={(e) =>
                    setNewReceipt({ ...newReceipt, amount: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <select
                  value={newReceipt.payment_method}
                  onChange={(e) =>
                    setNewReceipt({
                      ...newReceipt,
                      payment_method: e.target.value,
                    })
                  }
                  className="w-full h-11 px-3 border border-slate-300 bg-white rounded-md text-slate-900"
                >
                  <option value="neft">NEFT</option>
                  <option value="rtgs">RTGS</option>
                  <option value="cheque">Cheque</option>
                  <option value="upi">UPI</option>
                  <option value="cash">Cash</option>
                  <option value="imps">IMPS</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Receipt date</Label>
              <Input
                type="date"
                value={newReceipt.receipt_date}
                onChange={(e) =>
                  setNewReceipt({ ...newReceipt, receipt_date: e.target.value })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>Bank reference / UTR</Label>
              <Input
                value={newReceipt.bank_reference}
                onChange={(e) =>
                  setNewReceipt({
                    ...newReceipt,
                    bank_reference: e.target.value,
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>Narration</Label>
              <Input
                value={newReceipt.narration}
                onChange={(e) =>
                  setNewReceipt({ ...newReceipt, narration: e.target.value })
                }
              />
            </div>
          </div>
          <SheetFooter className="p-6 border-t flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button className="flex-1" onClick={submitReceipt}>
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};
